/**
 * The Auth0 invitation this page is carrying, if any.
 *
 * **The defect this exists to stop, which cost a real person their invitation.** An
 * Auth0 organization invitation email links to the application with
 * `?invitation=…&organization=…`. Those parameters are what make the sign-in an
 * ACCEPTANCE: without them Auth0 shows an ordinary login, the person signs in
 * perfectly successfully, and never joins the organization. The invitation then sits
 * unaccepted for ever, the People page keeps showing them as invited, and nothing
 * anywhere reports an error — the sign-in worked.
 *
 * That is exactly what happened: `ProtectedRoute` called `loginWithRedirect()` bare,
 * so an invitee landing on the app root was bounced into a plain login with the
 * parameters discarded. `login.js` forwarded them correctly, but the email does not
 * link there.
 *
 * So this is read from the live URL rather than from router state: the parameters
 * arrive on whatever route Auth0's email points at, which is configured in the Auth0
 * dashboard and is not ours to assume.
 */
export type InvitationParams = {
  invitation: string;
  organization?: string;
};

/** Reads them from the current URL. */
export function invitationFromUrl(
  search: string = typeof window === "undefined" ? "" : window.location.search
): InvitationParams | null {
  try {
    const params = new URLSearchParams(search);
    const invitation = params.get("invitation");

    // The invitation is the load-bearing one. An `organization` with no `invitation`
    // is not an acceptance, and passing it alone would ask Auth0 to log somebody in
    // to an organization rather than to accept anything.
    if (!invitation) return null;

    const organization = params.get("organization");

    return organization
      ? { invitation, organization }
      : { invitation };
  } catch {
    // A malformed query string is not an invitation. Never throw here: this is called
    // on the path that decides whether somebody can sign in at all.
    return null;
  }
}

/**
 * What to hand `loginWithRedirect` so a sign-in accepts the invitation it arrived with.
 *
 * Returns undefined when there is none, so a call site can spread it unconditionally
 * and an ordinary sign-in is unchanged.
 */
export function invitationLoginOptions(
  search?: string
): { authorizationParams: InvitationParams } | undefined {
  const invitation = invitationFromUrl(search);

  return invitation ? { authorizationParams: invitation } : undefined;
}
